import React from "react";
import { useOutletContext } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ArrowLeft,
  AlertCircle,
  Check,
  CheckCircle2,
  FileText,
  Home,
  LoaderCircle,
  MapPin,
  Send,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import "./HomebuyerOssReviewSubmitPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function formatDisplayLabel(value = "") {
  return String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatCountryLabel(value = "") {
  const normalized = String(value || "").toUpperCase();

  const countryMap = {
    BE: "Belgium",
    BELGIUM: "Belgium",
    ES: "Spain",
    SPAIN: "Spain",
  };

  return countryMap[normalized] || formatDisplayLabel(value);
}

function formatAddress(propertyDetails = {}) {
  const streetLine = [
    propertyDetails.address,
    propertyDetails.street,
    propertyDetails.street_name,
    propertyDetails.house_number,
    propertyDetails.number,
  ]
    .filter(Boolean)
    .join(" ");

  const cityLine = [
    propertyDetails.postal_code,
    propertyDetails.postcode,
    propertyDetails.zip_code,
    propertyDetails.city,
    propertyDetails.municipality,
    propertyDetails.region,
  ]
    .filter(Boolean)
    .join(", ");

  return [streetLine, cityLine].filter(Boolean).join(" · ");
}

function getMeasureLabel(measure) {
  if (typeof measure === "string") return formatDisplayLabel(measure);

  return (
    measure?.label ||
    measure?.name ||
    measure?.measure_label ||
    measure?.measure_name ||
    measure?.code ||
    "—"
  );
}

function getBudgetNumericValue(value = "") {
  const numeric = String(value || "").replace(/[^\d]/g, "");
  return numeric || null;
}

function ErrorToast({ message = "", closing = false, onClose }) {
  if (!message) return null;

  return (
    <div className={`hb-oss-review-toast ${closing ? "is-closing" : ""}`}>
      <div className="hb-oss-review-toast__content">
        <AlertCircle size={18} />
        <span>{message}</span>
      </div>

      <button type="button" onClick={onClose}>
        <X size={15} />
      </button>
    </div>
  );
}

export default function HomebuyerOssReviewModal({
  selectedOss,

  propertyDetails = {},

  selectedMeasures = [],

  notes = "",

  epcFileName = "",

  dossierId = null,

  marketRegionId = null,

  marketRegion = null,

  requestType = "RENOVATION",

  prePurchaseDetails = {},
  changeOssMode = false,
  previousOssRequestId = null,
  previousOssName = "",

  onClose,

  onSent,
}) {
  const { t } = useTranslation("homebuyer-oss-review-submit");
  const { getAccessTokenSilently } = useAuth0();

  const { refreshHomeownerMe } = useOutletContext() || {};

  const isPrePurchase = requestType === "PRE_PURCHASE";

  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);

  const [toastError, setToastError] = React.useState("");
  const [isToastClosing, setIsToastClosing] = React.useState(false);

  React.useEffect(() => {
    if (!toastError) return undefined;

    setIsToastClosing(false);

    const closeTimer = window.setTimeout(() => {
      setIsToastClosing(true);
    }, 4200);

    const removeTimer = window.setTimeout(() => {
      setToastError("");
      setIsToastClosing(false);
    }, 4550);

    return () => {
      window.clearTimeout(closeTimer);
      window.clearTimeout(removeTimer);
    };
  }, [toastError]);

  function handleClose() {
    if (submitting || isClosing || sent) return;

    setIsClosing(true);

    window.setTimeout(() => {
      onClose?.();
    }, 220);
  }

  async function handleSubmitRequest() {
    if (!selectedOss) {
      setToastError(t("errors.noOssSelected"));
      return;
    }

    if (changeOssMode && !previousOssRequestId) {
      setToastError(t("changeOss.missingPreviousRequest", "Unable to identify the current OSS connection. Go back to your dashboard and try again."));
      return;
    }

    try {
      setSubmitting(true);
      setToastError("");

      const token = await getAccessTokenSilently();

      const payload = {
        dossier_id: dossierId || null,

        market_region_id:
          marketRegionId ||
          propertyDetails?.market_region_id ||
          propertyDetails?.market_region?.id ||
          selectedOss?.market_region?.id ||
          null,

        oss_organisation_id: selectedOss.organisation_id || selectedOss.id,

        request_type: requestType,

        notes: isPrePurchase ? prePurchaseDetails.notes || "" : notes,
        ...(changeOssMode
          ? { replace_existing_request_id: previousOssRequestId }
          : {}),
      };

      if (isPrePurchase) {
        payload.preferred_location =
          prePurchaseDetails.preferred_location || "";
        payload.property_type_pref = prePurchaseDetails.property_type || "";
        payload.timeline = prePurchaseDetails.timeline || "";
        payload.budget_range_text = prePurchaseDetails.budget_range || "";
        payload.budget_min = getBudgetNumericValue(
          prePurchaseDetails.budget_range,
        );
        payload.extra_notes = prePurchaseDetails.notes || "";
      } else {
        payload.property_id =
          propertyDetails.id || propertyDetails.property_id || null;
        payload.property_details = propertyDetails;
        payload.selected_measures = selectedMeasures;
        payload.epc_file_name = epcFileName;
      }

      const res = await fetch(`${API_BASE}/api/homeowner/oss-requests/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || t("errors.submitFailed"));
      }

      if (typeof refreshHomeownerMe === "function") {
        try {
          await refreshHomeownerMe();
        } catch (refreshError) {}
      }

      setSent(true);

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });

      window.setTimeout(() => {
        onSent?.();
      }, 2500);
    } catch (err) {
      setToastError(err?.message || t("errors.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  React.useEffect(() => {
    function handleEsc(e) {
      if (e.key === "Escape") {
        handleClose();
      }
    }

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [submitting, isClosing, sent]);

  return (
    <div
      className={`hb-oss-review-modal-backdrop ${
        isClosing ? "is-closing" : ""
      }`}
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <ErrorToast
        message={toastError}
        closing={isToastClosing}
        onClose={() => {
          setIsToastClosing(true);
          window.setTimeout(() => {
            setToastError("");
            setIsToastClosing(false);
          }, 350);
        }}
      />

      <div
        className={`hb-oss-review-modal ${
          sent ? "hb-oss-review-modal--success" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {sent ? (
          <div className="hb-oss-review-modal__success">
            <div>
              <div className="hb-oss-review-modal__success-icon">
                <Check size={42} />
              </div>

              <h2>{t("success.title")}</h2>
              <p>
                {t("success.subtitle", {
                  ossName:
                    selectedOss?.organisation_name || t("success.theOss"),
                })}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="hb-oss-review-modal__header">
              <div className="hb-oss-review-modal__header-icon">
                <Send size={20} />
              </div>

              <div>
                <h2>{changeOssMode
                  ? t("changeOss.reviewTitle", "Confirm OSS change")
                  : t("title")}</h2>
                <p>{changeOssMode
                  ? t("changeOss.reviewSubtitle", "Review the new organisation before sending your replacement request.")
                  : t("subtitle")}</p>
              </div>
            </div>

            {changeOssMode ? (
              <section className="hb-oss-review-modal__box" role="status">
                <strong>{t("changeOss.handoverTitle", "Changing your One-Stop Shop")}</strong>
                <p>{t("changeOss.handoverMessage", {
                  defaultValue: "After you submit, {{oldName}} will be disconnected and the new organisation will receive a request. Your existing renovation project, invoices and financing records remain available. The new organisation will gain project access only after accepting.",
                  oldName: previousOssName || t("changeOss.currentOss", "your current One-Stop Shop"),
                })}</p>
              </section>
            ) : null}
            <section className="hb-oss-review-modal__box hb-oss-review-modal__box--selected">
              <div className="hb-oss-review-modal__box-heading">
                <CheckCircle2 size={17} />
                <span>{t("sections.selectedOss")}</span>
              </div>

              <p className="hb-oss-review-modal__oss-name">
                {selectedOss?.organisation_name || "—"}
              </p>

              <p className="hb-oss-review-modal__meta">
                <MapPin size={15} />
                {selectedOss?.market_region?.name || marketRegion?.name || "—"}
                {selectedOss?.country_code
                  ? ` · ${formatCountryLabel(selectedOss.country_code)}`
                  : ""}
              </p>
            </section>

            {isPrePurchase ? (
              <>
                <section className="hb-oss-review-modal__box">
                  <div className="hb-oss-review-modal__box-heading">
                    <MapPin size={17} />
                    <span>
                      {t("sections.searchCriteria", "Search criteria")}
                    </span>
                  </div>

                  <div className="hb-oss-review-modal__info-grid">
                    <InfoItem
                      label={t(
                        "fields.preferredLocation",
                        "Preferred location",
                      )}
                      value={formatDisplayLabel(
                        prePurchaseDetails.preferred_location,
                      )}
                    />
                    <InfoItem
                      label={t("fields.propertyType", "Property type")}
                      value={formatDisplayLabel(
                        prePurchaseDetails.property_type,
                      )}
                    />
                    <InfoItem
                      label={t("fields.timeline", "Timeline")}
                      value={formatDisplayLabel(prePurchaseDetails.timeline)}
                    />
                    <InfoItem
                      label={t("fields.budgetRange", "Budget range")}
                      value={
                        prePurchaseDetails.budget_range
                          ? `${prePurchaseDetails.budget_range} €`
                          : ""
                      }
                    />
                  </div>
                </section>

                {prePurchaseDetails.notes ? (
                  <section className="hb-oss-review-modal__box">
                    <div className="hb-oss-review-modal__box-heading">
                      <FileText size={17} />
                      <span>{t("sections.notes", "Additional notes")}</span>
                    </div>

                    <p className="hb-oss-review-modal__notes">
                      {prePurchaseDetails.notes}
                    </p>
                  </section>
                ) : null}
              </>
            ) : (
              <>
                <section className="hb-oss-review-modal__box">
                  <div className="hb-oss-review-modal__box-heading">
                    <Home size={17} />
                    <span>{t("sections.propertyInformation")}</span>
                  </div>

                  <div className="hb-oss-review-modal__info-grid">
                    <InfoItem
                      label={t("fields.address")}
                      value={formatAddress(propertyDetails)}
                    />
                    <InfoItem
                      label={t("fields.country")}
                      value={formatCountryLabel(
                        propertyDetails.country ||
                          propertyDetails.country_code ||
                          propertyDetails.country_of_property_purchase,
                      )}
                    />
                    <InfoItem
                      label={t("fields.propertyType")}
                      value={formatDisplayLabel(propertyDetails.property_type)}
                    />
                    <InfoItem
                      label={t("fields.yearBuilt")}
                      value={propertyDetails.year_of_build}
                    />
                    <InfoItem
                      label={t("fields.floorArea")}
                      value={
                        propertyDetails.surface_sqm
                          ? `${propertyDetails.surface_sqm} m²`
                          : ""
                      }
                    />
                    <InfoItem
                      label={t("fields.currentEpc")}
                      value={
                        propertyDetails.epc_rating_current ||
                        epcFileName ||
                        t("common.notProvided")
                      }
                    />
                    <InfoItem
                      label={t("fields.heatingType")}
                      value={formatDisplayLabel(propertyDetails.heating_type)}
                    />
                    <InfoItem
                      label={t("fields.coolingType")}
                      value={formatDisplayLabel(propertyDetails.cooling_type)}
                    />
                  </div>
                </section>

                <section className="hb-oss-review-modal__box">
                  <div className="hb-oss-review-modal__box-heading">
                    <Sparkles size={17} />
                    <span>{t("sections.mainImprovements")}</span>
                  </div>

                  {selectedMeasures.length > 0 ? (
                    <div className="hb-oss-review-modal__chips">
                      {selectedMeasures.map((measure) => (
                        <span
                          key={
                            measure.id ||
                            measure.code ||
                            getMeasureLabel(measure)
                          }
                        >
                          {getMeasureLabel(measure)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="hb-oss-review-modal__empty">
                      {t("messages.noMeasures")}
                    </p>
                  )}
                </section>

                {notes ? (
                  <section className="hb-oss-review-modal__box">
                    <div className="hb-oss-review-modal__box-heading">
                      <FileText size={17} />
                      <span>{t("sections.notes", "Additional notes")}</span>
                    </div>

                    <p className="hb-oss-review-modal__notes">{notes}</p>
                  </section>
                ) : null}
              </>
            )}

            <section className="hb-oss-review-modal__box hb-oss-review-modal__box--shared">
              <div className="hb-oss-review-modal__box-heading">
                <ShieldCheck size={17} />
                <span>{t("sections.sharedWithOss")}</span>
              </div>

              <div className="hb-oss-review-modal__shared-list">
                <span>{t("shared.contactInformation")}</span>

                {isPrePurchase ? (
                  <>
                    <span>
                      {t("shared.searchCriteria", "Property search criteria")}
                    </span>
                    <span>
                      {t("shared.budgetTimeline", "Budget and timeline")}
                    </span>
                    {prePurchaseDetails.notes ? (
                      <span>{t("shared.notes")}</span>
                    ) : null}
                  </>
                ) : (
                  <>
                    <span>{t("shared.propertyInformation")}</span>
                    <span>{t("shared.selectedMeasures")}</span>
                    <span>{t("shared.epcInformation")}</span>
                    {notes ? <span>{t("shared.notes")}</span> : null}
                  </>
                )}
              </div>
            </section>

            <div className="hb-oss-review-modal__actions">
              <button
                type="button"
                className="hb-oss-review-modal__secondary"
                onClick={handleClose}
                disabled={submitting}
              >
                <ArrowLeft size={15} />
                {t("buttons.back")}
              </button>

              <button
                type="button"
                className="hb-oss-review-modal__primary"
                onClick={handleSubmitRequest}
                disabled={submitting}
              >
                {submitting ? (
                  <LoaderCircle className="hb-oss-modal-spin" size={16} />
                ) : (
                  <Send size={15} />
                )}
                {submitting
                  ? t("buttons.sending")
                  : changeOssMode
                    ? t("changeOss.submit", "Request OSS change")
                    : t("buttons.confirm")}
              </button>
            </div>

            <p className="hb-oss-review-modal__note">{t("footerNote")}</p>
          </>
        )}
      </div>
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div className="hb-oss-review-modal__info-item">
      <span>{label}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}
